import { cloneDeep, groupBy } from "lodash"
import moment from "moment"

import {
  GetOrderDetailsResponseType,
  OrderItemType,
  ProductCategoryTypes,
  ProductType,
} from "../../types/OrderTypes"
import { IDomain } from "../../types/UserTypes"
import { GetAllNotesResponseType } from "../../types/ViewOrderTypes"
import { getDayDifference, phpToMoment } from "../../utils/Common"
import { getDomainCurrency } from "../../utils/TenantHelper"

import {
  AddEditCartItemRequestType,
  addonDetailsType,
  CartItemType,
  GetCartResponseType,
  GetParticipantsFromCart,
  MoreOptionType,
  PerBookingFieldTypeInBookingFields,
  ProductSummaryAccordianProps,
  UpdateParticipantsInCartPayloadType,
} from "./types"

// This function to get the participants payload for each cart_item.
// It is used when we add more than once product to the cart.
// TODO: Make the backend change so that the update doesn't required the old data
export const getCartItemsParticipantsPayload = ({
  cartData,
  participantsDetails,
}: {
  cartData?: GetCartResponseType["data"]
  participantsDetails?: GetParticipantsFromCart["data"]
}) => {
  const finalPayload: Record<string, UpdateParticipantsInCartPayloadType[]> = {}
  cartData?.items
    .filter(
      (ci) =>
        ci.product.type.internal_type !== ProductCategoryTypes.ADD_ONS &&
        ci.product.type.internal_type !== ProductCategoryTypes.EXPENSE
    )
    .forEach((cartItem) => {
      const payload: UpdateParticipantsInCartPayloadType[] = []
      participantsDetails
        ?.filter((cartItemFields) => cartItemFields.cart_item_id === cartItem.id)
        .forEach((cartItemUnitParticaipantFields) => {
          payload.push({
            unit_id: cartItemUnitParticaipantFields.unit.id,
            cart_participant_id: cartItemUnitParticaipantFields.id,
            fields: cartItemUnitParticaipantFields.fields.map((participantField) => ({
              product_field_id: participantField.id,
              value: participantField.value ?? "",
            })),
          })
        })
      if (payload.length) finalPayload[cartItem.id] = payload
    })
  return finalPayload
}

export const getAddOnPricing = async (
  cartData?: GetCartResponseType["data"],
  orderData?: GetOrderDetailsResponseType
) => {
  const l = (cartData || orderData)?.items
    ?.filter((item) => item.parent_id)
    ?.map((item) => {
      return {
        ...item,
        availability: item?.product?.merchandise?.availability,
      }
    })

  const availabilityByData = groupBy(l, "availability")
  const participants = availabilityByData.participant ?? []
  const customers = availabilityByData.customer ?? []
  const uniqueCartParticipantIds = Array.from(
    new Set(participants.map((item) => (item as CartItemType).cart_participant_id))
  )

  const uniqueCustomerUnits = new Set<string>()
  const uniqueCustomerProducts = new Set<string>()
  customers.forEach((item) => {
    uniqueCustomerProducts.add(item?.product?.id as string)
    item.quantities.forEach((quantity: any) => {
      uniqueCustomerUnits.add(quantity.unit.id) // Assuming "unit" has a "name" property
    })
  })
  const uniqueCustomerUnitsIds = Array.from(uniqueCustomerUnits)
  const uniqueCustomerProductsIds = Array.from(uniqueCustomerProducts)

  const uniqueUnits = new Set()
  const uniqueProducts = new Set()
  participants.forEach((item) => {
    uniqueProducts.add(item?.product?.id as string)
    item.quantities.forEach((quantity: any) => {
      uniqueUnits.add(quantity.unit.id) // Assuming "unit" has a "name" property
    })
  })
  const uniqueUnitsIds = Array.from(uniqueUnits)
  const uniqueProductsIds = Array.from(uniqueProducts)

  const data: any = {}
  uniqueProductsIds.forEach((productsId) => {
    uniqueCartParticipantIds.forEach((participantIds) => {
      uniqueUnitsIds.forEach((unitIds) => {
        const foundItem = l?.find((item: any) => {
          return (
            item.cart_participant_id === participantIds &&
            item.product.id === productsId &&
            item.quantities.length > 0 && // Ensure quantities array is not empty
            item.quantities[0].unit.id === unitIds
          )
        })
        data[`${productsId}-${unitIds}-${participantIds}`] = foundItem?.formatted_total
      })
    })
  })

  uniqueCustomerProductsIds.forEach((productsId) => {
    customers.forEach(() => {
      uniqueCustomerUnitsIds.forEach((unitIds) => {
        const foundItem = l?.find((item: any) => {
          return (
            item.cart_participant_id === null &&
            item.product.id === productsId &&
            item.quantities.length > 0 && // Ensure quantities array is not empty
            item.quantities[0].unit.id === unitIds
          )
        })
        data[`${productsId}-${unitIds}-undefined`] = foundItem?.formatted_total
      })
    })
  })
  return data
}

export const getParticipantFieldsInfoOfCartItem = ({
  cartItem,
  participantsDetails,
}: {
  cartItem?: CartItemType
  participantsDetails?: GetParticipantsFromCart["data"]
}) => {
  if (typeof cartItem === "undefined") return []
  const fields: Record<string, boolean | string | string[] | number>[] = []
  let participantNumber = 1
  let prevUnitId = ""
  participantsDetails
    ?.filter((pf) => pf.cart_item_id === cartItem?.id)
    .forEach((participant) => {
      if (prevUnitId === participant.unit.id) {
        participantNumber += 1
      } else {
        participantNumber = 1
        prevUnitId = participant.unit.id
      }
      const participantFields: Record<string, boolean | string | string[] | number> = {}
      participant?.fields?.forEach((field) => {
        if (field?.allowed_internal_booking && field?.ask?.includes("ask_per_participant")) {
          participantFields[field?.field?.name] = field?.value
            ? field.field.type === "checkbox"
              ? Array.isArray(field.value)
                ? field.value
                : JSON.parse(`${field.value ?? []}`)
              : field.value
            : field?.field?.type === "checkbox"
              ? []
              : ""
          participantFields[`${field?.field?.name}_id`] = field?.id ?? ""
        }
      })
      participantFields.participantGroupName = participant?.unit?.name
        ? `${participant.unit.name} ${participantNumber}`
        : "Participant"
      participantFields.participantUnitId = participant?.unit?.id ?? ""
      participantFields.id = participant?.id
      fields.push(participantFields)
    })
  return fields
}

export const getBookingFieldsInfoOfCartItem = ({
  cartItem,
  orderItem,
  perBookingFieldsDetails,
}: {
  cartItem?: CartItemType
  orderItem?: OrderItemType
  perBookingFieldsDetails?: PerBookingFieldTypeInBookingFields[]
}) => {
  if (
    (typeof cartItem === "undefined" && typeof orderItem === "undefined") ||
    perBookingFieldsDetails?.length === 0
  )
    return []
  const perBookingFields: Record<string, boolean | string | string[] | number> = {}
  perBookingFieldsDetails
    ?.filter((pf) => pf.cart_item_id === cartItem?.id || pf.order_item_id === orderItem?.id)
    .forEach((bookingField) => {
      perBookingFields[bookingField.field_dump.product_field.field.name] = bookingField.value
        ? bookingField.field_dump.product_field.field.type === "checkbox"
          ? Array.isArray(bookingField.value)
            ? bookingField.value
            : JSON.parse(bookingField.value as string)
          : bookingField.value
        : bookingField.field_dump.product_field.field.type === "checkbox"
          ? []
          : ""
      perBookingFields[`${bookingField.field_dump.product_field.field.name}_id`] =
        bookingField.field_dump.product_field.id ?? ""
      if (orderItem)
        perBookingFields[
          `${bookingField.field_dump.product_field.field.name}_order_item_field_id`
        ] = bookingField?.id ?? ""
    })
  return Object.keys(perBookingFields).length ? [perBookingFields] : []
}

export const getAddonsInfoOfCartItem = ({
  cartData,
  cartItem,
  orderData,
  orderItem,
}: {
  cartItem?: CartItemType
  cartData?: GetCartResponseType["data"]
  orderItem?: OrderItemType
  orderData?: GetOrderDetailsResponseType
}): addonDetailsType[] => {
  const groupedAddons: addonDetailsType[] = []
  ;(cartData || orderData)?.items
    .filter(
      (ci) =>
        ci.product.type.internal_type === ProductCategoryTypes.ADD_ONS &&
        (ci.parent_id === cartItem?.id || ci.parent_id === orderItem?.id)
    )
    .forEach((addon) => {
      const addonProductIndex = groupedAddons.findIndex(
        (ga) => ga?.addonId && addon?.product?.id && ga.addonId === addon.product.id
      )
      if (addonProductIndex >= 0 && addon?.quantities?.length) {
        groupedAddons[addonProductIndex]?.units?.push({
          id: addon.id,
          unitCount: addon.quantities[0].quantity,
          unitName: addon.quantities[0].unit?.name,
          unitId: addon.quantities[0].unit?.id,
          participantId:
            (addon as CartItemType).cart_participant_id ??
            (addon as OrderItemType).booking_participant_id ??
            undefined,
        })
      } else {
        groupedAddons.push({
          addonId: addon.product.id,
          availability: (addon.product.merchandise?.availability ??
            (addon as OrderItemType).product.meta?.merchandise
              ?.availability) as addonDetailsType["availability"],
          requirement: (addon.product.merchandise?.requirements ??
            (addon as OrderItemType).product.meta?.merchandise
              ?.requirements) as addonDetailsType["requirement"],
          units: addon.quantities.map((unit) => ({
            id: addon.id,
            unitCount: unit.quantity,
            unitName: unit.unit?.name,
            unitId: unit.unit?.id,
            participantId:
              (addon as CartItemType).cart_participant_id ??
              (addon as OrderItemType).booking_participant_id ??
              undefined,
          })),
        })
      }
    })
  return groupedAddons
}

export const getGroupedAddonsOfACartItem = ({
  cartItem,
  cartData,
}: {
  cartItem: CartItemType
  cartData?: GetCartResponseType["data"]
}) => {
  const groupedAddons: CartItemType[] = []
  if (cartData && cartItem) {
    cloneDeep(cartData)
      ?.items.filter(
        (ci) =>
          ci.product.type.internal_type === ProductCategoryTypes.ADD_ONS &&
          ci.parent_id === cartItem.id
      )
      .forEach((addon) => {
        const addonProductIndex = groupedAddons.findIndex(
          (ga) => ga?.product?.id && addon?.product?.id && ga.product.id === addon.product.id
        )
        if (addonProductIndex >= 0 && addon?.quantities?.length) {
          groupedAddons[addonProductIndex]?.quantities?.push(...addon.quantities)
        } else {
          groupedAddons.push(addon)
        }
      })
  }
  return groupedAddons
}

export const handleShowMultiDay = (startDate: string, endDate: string) => {
  const start_Date = moment(startDate)
  const end_date = moment(endDate)

  const dayDifference = getDayDifference(start_Date, end_date) + 1

  return `${dayDifference} Day${dayDifference > 1 ? "s" : ""} `
}

const getCartItemAccordionDataByType = ({
  cartItem,
  perBookingFieldsDetails,
  participantsDetails,
  domainDetails,
  cartData,
}: {
  cartItem: CartItemType
  perBookingFieldsDetails?: PerBookingFieldTypeInBookingFields[]
  participantsDetails?: GetParticipantsFromCart["data"]
  domainDetails?: IDomain
  cartData: GetCartResponseType["data"]
}) => {
  if (cartItem.product.type.internal_type === ProductCategoryTypes.GIFT_CARD) {
    const fieldsInfo = perBookingFieldsDetails?.filter(
      (field) => field.cart_item_id === cartItem.id
    )
    return {
      productType: cartItem.product.type.internal_type,
      amount: cartItem.formatted_subtotal,
      expiryDate:
        cartItem.product.gift_card?.validity?.expiry_type === "fixed_date"
          ? cartItem.product.gift_card?.validity.expiry_value
          : cartItem.product.gift_card?.validity?.expiry_type === "fixed_days"
            ? `${cartItem.product.gift_card?.validity.expiry_value ?? 0} days after purchase`
            : "Never",
      recipientEmail:
        fieldsInfo?.find((field) => field.field.label === "Recipient Email")?.value ?? "-",
      to: fieldsInfo?.find((field) => field.field.label === "To")?.value ?? "-",
      from: fieldsInfo?.find((field) => field.field.label === "From")?.value ?? "-",
      deliveryDate:
        fieldsInfo?.find((field) => field.field.label === "Delivery Date")?.value ?? "-",
      message: fieldsInfo?.find((field) => field.field.label === "Message")?.value ?? "-",
    }
  } else if (cartItem.product.type.internal_type === ProductCategoryTypes.MERCHANDIZE) {
    return {
      productType: cartItem.product.type.internal_type,
      units: cartItem.quantities.map((unitGroup) => ({
        unitName: unitGroup.unit?.name,
        unitId: unitGroup.unit?.id,
        pricePerParticipant: unitGroup.formatted_price,
        totalPricePerUnitGroup: unitGroup.formatted_total,
        participantsCount: unitGroup.quantity,
        unitParticipants: getParticipantFieldsInfoOfCartItem?.({
          cartItem,
          participantsDetails,
        })
          .filter(
            (participantDetails) => participantDetails.participantUnitId === unitGroup.unit?.id
          )
          .map((participantDetails) => ({
            name: participantDetails?.["first-name"]
              ? `${participantDetails?.["first-name"]} ${participantDetails?.["last-name"] ?? ""}`
              : (participantDetails.participantGroupName as string),
            id: participantDetails.id as string,
          })),
      })),
    }
  } else if (cartItem.product.type.internal_type === ProductCategoryTypes.MEMBERSHIP) {
    return {
      productType: cartItem.product.type.internal_type,
      amount: cartItem.formatted_subtotal,
      membershipValidaityInfo: {
        expiryStartDate: moment(
          cartItem.product?.membership?.validity?.start_at,
          "YYYY-MM-DD"
        ).format(phpToMoment(domainDetails?.date_format || "Y-m-d")),
        expiryEndDate: moment(cartItem.product?.membership?.validity?.end_at, "YYYY-MM-DD").format(
          phpToMoment(domainDetails?.date_format || "Y-m-d")
        ),
        expiryValue: cartItem.product?.membership?.validity?.expiry_value,
        expiryType: cartItem.product?.membership?.validity?.expiry_type,
      },
      membershipDiscounts: cartItem.product.membershipDiscounts?.map((discount) => ({
        label: `${discount.title} - ${discount.amount_type === "percentage" ? `${discount.amount}%` : `${getDomainCurrency()}${discount.amount}`}`,
        id: discount.id,
      })),
      membershipOnlyProducts: cartItem.product?.membershipProducts
        ?.filter((product) => product?.membership_product?.id === cartItem.product.id)
        .map((product) => product.product),
      membershipLimitation: cartItem.product?.membership?.limitation
        ? {
            duration_type: cartItem.product.membership.limitation.duration_type,
            duration_value: cartItem.product.membership.limitation.duration_value,
            max_no_of_booking: cartItem.product.membership.limitation.max_no_of_booking,
            has_booking_limitation: cartItem.product.membership.limitation.has_booking_limitation,
          }
        : undefined,
      membershipHasProductLimitations: Boolean(cartItem.product?.membershipProductLimitationCount),
    }
  }
  return {
    productType: cartItem.product.type.internal_type,
    activityDuration: !cartItem?.product?.is_dynamic_rental
      ? `${cartItem.product?.availability?.duration ?? ""} ${cartItem.product?.availability?.duration_type ?? ""}`
      : "",
    orderedDate: cartItem?.quantities?.[0]?.unit?.is_multi_day
      ? `
        ${moment(cartItem.product_availability_slot?.local_start_time).format(
          phpToMoment(domainDetails?.date_format || "Y-m-d")
        )} (${moment(cartItem.product_availability_slot?.local_start_time).format("hh:mmA")}) - ${moment(
          cartItem.product_availability_slot?.local_end_time
        ).format(phpToMoment(domainDetails?.date_format || "Y-m-d"))} ${
          cartItem.product_availability_slot?.local_end_time
            ? `(${moment(cartItem.product_availability_slot?.local_end_time).format("hh:mmA")})`
            : ""
        }
      `
      : cartItem.product_availability_slot &&
        moment(cartItem.product_availability_slot?.local_start_time).format(
          phpToMoment(domainDetails?.date_format || "Y-m-d")
        ),
    orderedDaysCount: cartItem?.quantities?.[0]?.unit?.is_multi_day
      ? handleShowMultiDay?.(
          cartItem?.product_availability_slot?.local_start_time ?? "",
          cartItem?.product_availability_slot?.local_end_time ?? ""
        )
      : "",
    orderedDuration: cartItem?.quantities?.[0]?.unit?.is_multi_day
      ? ""
      : cartItem?.product_availability_slot?.local_start_time
        ? `${moment(cartItem?.product_availability_slot?.local_start_time).format("hh:mmA")}${
            cartItem?.product_availability_slot?.local_end_time
              ? ` - ${moment(cartItem?.product_availability_slot?.local_end_time).format("hh:mmA")}`
              : ""
          }`
        : "",
    addons: getGroupedAddonsOfACartItem?.({ cartItem, cartData })?.map((addon) => ({
      id: addon.product.id,
      name: addon.product.name,
      requirement: addon.product.merchandise?.requirements,
      units: addon.quantities?.map((unitGroup) => ({
        unitName: unitGroup.unit?.name,
        unitId: unitGroup.unit?.id,
        pricePerParticipant: unitGroup.formatted_price,
        totalPricePerUnitGroup: unitGroup.formatted_total,
        participantsCount: unitGroup.quantity,
      })),
    })),
    units: cartItem.quantities.map((unitGroup) => ({
      unitName: unitGroup.unit?.name,
      unitId: unitGroup.unit?.id,
      pricePerParticipant: unitGroup.formatted_price,
      totalPricePerUnitGroup: unitGroup.formatted_total,
      participantsCount: unitGroup.quantity,
      unitParticipants: getParticipantFieldsInfoOfCartItem?.({
        cartItem,
        participantsDetails,
      })
        .filter((participantDetails) => participantDetails.participantUnitId === unitGroup.unit?.id)
        .map((participantDetails) => ({
          name: participantDetails?.["first-name"]
            ? `${participantDetails?.["first-name"]} ${participantDetails?.["last-name"] ?? ""}`
            : (participantDetails.participantGroupName as string),
          id: participantDetails.id as string,
        })),
    })),
    perBookingFields: perBookingFieldsDetails
      ?.filter((field) => field.cart_item_id === cartItem.id)
      .map((field) => ({
        label: field.label,
        value: field.value,
        type: field.field_dump?.product_field?.field?.type,
        options: field.field_dump.product_field.options,
      })),
    expenseDiscounts: cartData?.items
      ?.filter(
        (ci) =>
          ci.product.type.internal_type === ProductCategoryTypes.EXPENSE &&
          ci.parent_id === cartItem.id
      )
      ?.map((expenseDiscountCartItem) => ({
        id: expenseDiscountCartItem.id,
        description: expenseDiscountCartItem.description ?? "",
        amount: expenseDiscountCartItem.total ?? 0,
        formatted_amount: expenseDiscountCartItem.formatted_subtotal,
        levies: expenseDiscountCartItem.levies,
      })),
  }
}

export const getFormattedCartItemData = ({
  cartData,
  domainDetails,
  notesData,
  participantsDetails,
  perBookingFieldsDetails,
  dropdownOptions,
  handleEditExpenseDiscount,
  handleDeleteItem,
}: {
  cartData?: GetCartResponseType["data"]
  domainDetails?: IDomain
  notesData?: GetAllNotesResponseType[]
  perBookingFieldsDetails?: PerBookingFieldTypeInBookingFields[]
  participantsDetails?: GetParticipantsFromCart["data"]
  handleEditExpenseDiscount?: (arg0: { item: CartItemType; parentId: string }) => void
  handleDeleteItem?: (arg0: { id: string; name: string }) => void
  dropdownOptions?: (arg0: {
    isExpenseDiscountEnabled: boolean
    isAddonEnabled: boolean
    cartItem: CartItemType
  }) => {
    merchandise?: MoreOptionType[]
    giftcard?: MoreOptionType[]
    membership?: MoreOptionType[]
    dynamicRentals?: MoreOptionType[]
    bookable?: MoreOptionType[]
  }
  enableSwapResource: boolean
}) => {
  if (cartData?.items?.length) {
    const cartItemData: ProductSummaryAccordianProps[] = []
    cartData.items
      .filter(
        (ci) =>
          ci.product.type.internal_type !== ProductCategoryTypes.ADD_ONS &&
          ci.product.type.internal_type !== ProductCategoryTypes.EXPENSE
      )
      .forEach((cartItem) => {
        const isAddonEnabled =
          cartItem?.product?.type?.internal_type === ProductCategoryTypes.BOOKABLE &&
          cartItem?.product.has_addon
        const isExpenseDiscountEnabled =
          cartItem?.product?.type?.internal_type === ProductCategoryTypes.BOOKABLE &&
          domainDetails?.enable_expense_discount
        const dropdownOptionsByProductType = dropdownOptions?.({
          isAddonEnabled: Boolean(isAddonEnabled),
          isExpenseDiscountEnabled: Boolean(isExpenseDiscountEnabled),
          cartItem,
        })

        cartItemData.push({
          ...(getCartItemAccordionDataByType({
            cartItem,
            cartData,
            domainDetails,
            participantsDetails,
            perBookingFieldsDetails,
          }) as any),
          ...{
            id: cartItem.product.id,
            cartItemId: cartItem.id,
            productName: cartItem.product.name,
            totalAmount: cartItem.formatted_subtotal,
            membershipDiscount:
              cartItem.membership_discount && cartData?.customerMembership?.product?.name
                ? {
                    label: `${cartData.customerMembership.product.name} ${cartItem.membership_discount_json?.amount_type === "percentage" ? `(${cartItem.membership_discount_json.amount}%)` : ""}`,
                    value: cartItem.formatted_membership_discount,
                  }
                : undefined,
            productNotes: notesData
              ?.filter((note) => note.notable.id === cartItem.id)
              ?.map((note) => ({
                content: note.content,
                id: note.id,
              })),
            productCategory: cartItem.product.categories?.[0]?.name ?? "",
            handleEditExpenseDiscount:
              domainDetails?.enable_expense_discount &&
              cartItem?.product.type.internal_type === ProductCategoryTypes.BOOKABLE &&
              handleEditExpenseDiscount
                ? (expenseDiscountCartItemId) => {
                    const expenseDiscountCartItem = cartData?.items?.find(
                      (ci) =>
                        ci.product.type.internal_type === ProductCategoryTypes.EXPENSE &&
                        ci.id === expenseDiscountCartItemId
                    )
                    if (expenseDiscountCartItem && expenseDiscountCartItem.parent_id) {
                      handleEditExpenseDiscount({
                        item: expenseDiscountCartItem,
                        parentId: expenseDiscountCartItem.parent_id,
                      })
                    } else {
                      console.log("Unable to find the expense/discount! please try again later.")
                    }
                  }
                : undefined,
            handleDeleteItem,
          },
          dropdownOptions: (cartItem.product.type.internal_type === ProductCategoryTypes.MERCHANDIZE
            ? (dropdownOptionsByProductType?.merchandise ?? [])
            : cartItem.product.type.internal_type === ProductCategoryTypes.GIFT_CARD
              ? (dropdownOptionsByProductType?.giftcard ?? [])
              : cartItem.product.is_dynamic_rental
                ? (dropdownOptionsByProductType?.dynamicRentals ?? [])
                : cartItem.product.type.internal_type === ProductCategoryTypes.BOOKABLE
                  ? (dropdownOptionsByProductType?.bookable ?? [])
                  : cartItem.product.type.internal_type === ProductCategoryTypes.MEMBERSHIP
                    ? (dropdownOptionsByProductType?.membership ?? [])
                    : []
          ).filter((option) => Boolean(option)),
        })
      })
    return cartItemData
  }
  return []
}

export const getAddBookableProductToCartPayload = ({
  selectedProduct,
  productInfo,
}: {
  // TODO: Need to define a type which works for both dynamic rental product and bookable activity product
  productInfo?: any
  selectedProduct: ProductType
}) => {
  return {
    product_id: selectedProduct?.availability?.product_id || selectedProduct?.id || "",
    ignore_call_to_book_closure: !!selectedProduct?.reopenable_for,
    quantities: (productInfo as any)?.is_single_day
      ? productInfo?.units?.map((unit: any) => ({
          unit_id: unit.unitId ?? "",
          quantity: unit.unitCount ? parseFloat(`${unit.unitCount}`) : 0,
          start_at: unit.start_at,
          end_at: unit.end_at,
        }))
      : productInfo?.units?.map((unit: any) => ({
          unit_id: unit.unitId ?? "",
          quantity: unit.unitCount ? parseFloat(`${unit.unitCount}`) : 0,
          start_at: unit.start_at,
          end_at: unit.end_at,
          availability_id: unit?.time,
        })),
    slot_id: productInfo?.time?.value || productInfo.slot_id || "",
    add_on: [],
  } as AddEditCartItemRequestType
}
